import { useCallback, useEffect, useState } from "react";
import { PROLOGUE_LINES } from "./act1.ts";
import { OptionsButton } from "./OptionsButton.tsx";
import { loc, type Loc } from "./loc.ts";
import { useGt } from "./gameText.ts";
import { useLang } from "../../gamzone/i18n.ts";

export const PROLOGUE_BG = "/assets/world-maps/act-1-moss-gate.webp";

const DEFAULT_LAST_HINT: Loc = { ko: "탭하여 지도로", en: "Tap for the map" };

/**
 * A full-bleed subtitle screen over a map background: one line at a time,
 * tap / click / Enter advances, `onDone` fires past the last line. Used for
 * the opening prologue (ACT1_PROLOGUE) and the 1막→2막 bridge (ACT1_TO_ACT2).
 */
export function Subtitles({
  lines = PROLOGUE_LINES,
  bg = PROLOGUE_BG,
  lastHint = DEFAULT_LAST_HINT,
  onDone,
}: {
  lines?: readonly Loc[];
  bg?: string;
  lastHint?: Loc;
  onDone: () => void;
}) {
  const lang = useLang();
  const gt = useGt();
  const [i, setI] = useState(0);
  const last = lines.length - 1;

  const advance = useCallback(() => {
    setI((n) => {
      if (n >= last) {
        onDone();
        return n;
      }
      return n + 1;
    });
  }, [last, onDone]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        advance();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [advance]);

  return (
    <div
      className="cam-screen cam-prologue"
      style={{ ["--cam-bg" as string]: `url(${bg})` }}
      role="button"
      tabIndex={0}
      aria-label={gt("nextLine")}
      onClick={advance}
    >
      <OptionsButton />

      <div className="cam-subtitle">
        <p key={i} className="cam-subtitle-line">
          {loc(lines[i], lang)}
        </p>
        {lines.length > 1 && (
          <div className="cam-progress" aria-hidden>
            {lines.map((_, n) => (
              <span key={n} className={n <= i ? "on" : ""} />
            ))}
          </div>
        )}
        <span className="cam-hint">{i < last ? gt("tapContinue") : loc(lastHint, lang)}</span>
      </div>
    </div>
  );
}
